import { useEffect, useRef, useState } from "react";

interface IntroVideoGateProps {
  onComplete: () => void;
}

// Module-level singleton guard — ensures only ONE IntroVideoGate instance
// ever owns the player, even under React StrictMode double-mounts.
let __introGateMounted = false;
let __introGateFinishing = false;

const VIMEO_ID = "1213714013";
// autoplay + unmuted attempt; Vimeo will fall back to muted autoplay if blocked.
const VIMEO_SRC = `https://player.vimeo.com/video/${VIMEO_ID}?autoplay=1&title=0&byline=0&portrait=0&pip=0&dnt=1&playsinline=1`;

type VimeoPlayer = {
  on: (event: string, cb: (...args: unknown[]) => void) => void;
  destroy: () => Promise<void>;
  unload: () => Promise<void>;
};

declare global {
  interface Window {
    Vimeo?: { Player: new (el: HTMLIFrameElement) => VimeoPlayer };
  }
}

const VIMEO_SCRIPT_SRC = "https://player.vimeo.com/api/player.js";

function loadVimeoScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.Vimeo?.Player) return resolve();
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${VIMEO_SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("vimeo script failed")));
      return;
    }
    const s = document.createElement("script");
    s.src = VIMEO_SCRIPT_SRC;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("vimeo script failed"));
    document.head.appendChild(s);
  });
}

/**
 * Plays the brand intro film (hosted on Vimeo) between the loading bar and
 * the listen page. Auto-advances on `ended`; a manual "Continue →" escape
 * hatch is always available.
 */
export default function IntroVideoGate({ onComplete }: IntroVideoGateProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const playerRef = useRef<VimeoPlayer | null>(null);
  const [exiting, setExiting] = useState(false);
  const [allowed] = useState(() => {
    if (__introGateMounted) return false;
    __introGateMounted = true;
    return true;
  });

  useEffect(() => {
    if (!allowed) {
      onComplete();
      return;
    }
    return () => {
      __introGateMounted = false;
      __introGateFinishing = false;
    };
  }, [allowed, onComplete]);

  useEffect(() => {
    if (!allowed) return;
    let cancelled = false;
    loadVimeoScript()
      .then(() => {
        if (cancelled || !iframeRef.current || !window.Vimeo) return;
        const p = new window.Vimeo.Player(iframeRef.current);
        playerRef.current = p;
        p.on("ended", () => finish());
      })
      .catch(() => {
        /* iframe still plays; user can Continue manually */
      });
    return () => {
      cancelled = true;
      const p = playerRef.current;
      if (p) {
        try { p.unload().catch(() => {}); } catch { /* ignore */ }
        try { p.destroy().catch(() => {}); } catch { /* ignore */ }
      }
      playerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed]);

  const finish = () => {
    if (exiting || __introGateFinishing) return;
    __introGateFinishing = true;
    setExiting(true);
    const p = playerRef.current;
    if (p) {
      try { p.unload().catch(() => {}); } catch { /* ignore */ }
    }
    onComplete();
  };

  if (!allowed) return null;

  return (
    <div
      className={`fixed inset-0 z-[150] bg-background overflow-y-auto overscroll-contain transition-opacity duration-300 ${
        exiting ? "opacity-0 pointer-events-none" : "opacity-100"
      }`}
      role="region"
      aria-label="Intro video"
    >
      <div className="min-h-full flex flex-col items-center justify-center py-12 px-4">
        <div
          className="relative w-full max-w-5xl"
          style={{ aspectRatio: "16 / 9", maxHeight: "80vh" }}
        >
          <iframe
            ref={iframeRef}
            src={VIMEO_SRC}
            title="Love Language FM intro"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            className="absolute inset-0 w-full h-full border-0"
          />
        </div>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            finish();
          }}
          className="mt-8 text-xs tracking-[0.2em] uppercase text-foreground/70 hover:text-foreground transition-colors px-4 py-2"
          style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 0.5rem)" }}
        >
          Continue →
        </button>
      </div>
    </div>
  );
}
